class MenuManager {
    constructor() {
        if (MenuManager.instance) {
            return MenuManager.instance;
        }

        this.activeMenus = new Map();
        this.menuSchedule = new Map();
        this.vueMenuEventBus = null;
        
        MenuManager.instance = this;
    }

    setVueEventBus(bus) {
        this.vueMenuEventBus = bus;
    }

    isVRMode() {
        return typeof VR !== 'undefined' && 
               VR.device && 
               VR.device.isPresenting && 
               VR.device.isPresenting();
    }

    registerMenu(actionId, startTime, endTime, menuType) {
        this.menuSchedule.set(actionId, {
            startTime: startTime || 0,
            endTime: endTime || Infinity,
            menuType: menuType,
            active: false
        });
    }

    hasOverlappingMenus(currentActionId, currentTime) {
        const currentSchedule = this.menuSchedule.get(currentActionId);
        if (!currentSchedule) return false;

        for (const [actionId, schedule] of this.menuSchedule.entries()) {
            if (actionId === currentActionId) continue;
            
            if (schedule.startTime < currentSchedule.endTime && schedule.startTime > currentTime) {
                return true;
            }
        }
        
        return false;
    }

    async createMenu(actionId, menuConfig) {
        const { menuType, style, content, scene } = menuConfig;
        
        const existingMenuIds = Array.from(this.activeMenus.keys());
        for (const existingId of existingMenuIds) {
            if (existingId !== actionId) {
                try {
                    await this.removeMenu(existingId, true);
                } catch (error) {
                    console.error(`MenuManager: Error removing existing menu ${existingId}:`, error);
                }
            }
        }
        
        const schedule = this.menuSchedule.get(actionId);
        if (schedule) schedule.active = true;

        const menuInstance = await this._createVueMenu(actionId, menuConfig);

        if (menuInstance) {
            this.activeMenus.set(actionId, {
                instance: menuInstance,
                config: menuConfig,
                createdAt: Date.now()
            });
        }

        // TODO: Create ThreeMeshUI menu for VR/AR mode
        // The Vue menu component will automatically hide itself in VR mode
        // ThreeMeshUI menu should be created here when in VR mode
        // if (this.isVRMode()) {
        //     await this._createThreeMeshUIMenu(actionId, menuConfig);
        // }

        return menuInstance;
    }

    async _createVueMenu(actionId, menuConfig) {
        if (!this.vueMenuEventBus) {
            console.error('MenuManager: Vue event bus not set');
            return null;
        }

        this.vueMenuEventBus.$emit('training:show-menu', {
            actionId: actionId,
            menuType: menuConfig.menuType,
            style: menuConfig.style,
            content: menuConfig.content,
            scene: menuConfig.scene
        });

        return {
            actionId: actionId,
            menuType: menuConfig.menuType,
            dispose: () => {
                this.vueMenuEventBus?.$emit('training:hide-menu', { actionId });
            }
        };
    }

    async removeMenu(actionId, force = false) {
        const menuData = this.activeMenus.get(actionId);
        const schedule = this.menuSchedule.get(actionId);

        if (!menuData) return;
        if (!force && this.hasOverlappingMenus(actionId, Date.now())) return;

        if (this.vueMenuEventBus) {
            this.vueMenuEventBus.$emit('training:hide-menu', { actionId });
        }

        if (menuData.instance && typeof menuData.instance.dispose === 'function') {
            try {
                menuData.instance.dispose();
            } catch (error) {
                console.error('MenuManager: Error disposing menu instance:', error);
            }
        }

        // TODO: Remove ThreeMeshUI menu for VR/AR mode
        // if (this.isVRMode()) {
        //     await this._removeThreeMeshUIMenu(actionId);
        // }

        this.activeMenus.delete(actionId);
        if (schedule) schedule.active = false;
    }

    async removeAllMenus() {
        const actionIds = Array.from(this.activeMenus.keys());
        for (const actionId of actionIds) {
            try {
                await this.removeMenu(actionId, true);
            } catch (error) {
                console.error(`MenuManager: Error removing menu ${actionId}:`, error);
            }
        }

        this.activeMenus.clear();
        this.menuSchedule.clear();

        if (this.vueMenuEventBus) this.vueMenuEventBus.$emit('training:hide-all-menus');

        // TODO: Remove all ThreeMeshUI menus for VR/AR mode
    }

    async updateMenu(actionId, updates) {
        const menuData = this.activeMenus.get(actionId);
        if (!menuData) return;

        try {
            const updatedConfig = {
                ...menuData.config,
                content: { ...menuData.config.content, ...updates.content },
                style: { ...menuData.config.style, ...updates.style }
            };

            await this.removeMenu(actionId, true);
            await this.createMenu(actionId, updatedConfig);
        } catch (error) {
            console.error('MenuManager: Error updating menu:', error);
        }
    }

    getActiveMenuCount() {
        return this.activeMenus.size;
    }

    getMenu(actionId) {
        return this.activeMenus.get(actionId);
    }

    isMenuActive(actionId) {
        return this.activeMenus.has(actionId);
    }

    async onVRModeChange() {
        // TODO: Handle VR mode transitions
        // When entering VR: Show ThreeMeshUI menus, hide Vue menus (via CSS)
        // When exiting VR: Hide ThreeMeshUI menus, show Vue menus (via CSS)
        // Vue menus always exist, just visibility changes
    }

    destroy() {
        this.removeAllMenus();
        this.vueMenuEventBus = null;
        MenuManager.instance = null;
    }
}

// Create singleton instance
const menuManager = new MenuManager();

// Make globally available
if (typeof window !== 'undefined') {
    window.MenuManager = menuManager;
}

export default menuManager;

